import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, filter, of, switchMap, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ReferralApi } from './data-access/referral.api';
import { ReferralModalService } from './data-access/referral-modal.service';
import { MyReferral, MyReferralResponse } from './models/referral.models';
import { ScrollLockService } from '../shared/service/scroll-lock.service';

type ReferralTab = 'invite' | 'rewards';

/** How long the body stays dimmed before the new tab is swapped in. Must match the
 * `duration-150` on the body container in the template. */
const TAB_FADE_MS = 150;

/**
 * "Refer & Earn" popup. Double-sided program: the referred user gets 20% off their first payment
 * (a Stripe coupon applied at checkout), and the referrer earns 20% of what the referred user
 * actually pays, up to $500. Both the Invite copy and the Info panel state both sides. Data comes
 * from GET /referral/me, fetched each time the modal opens so the reward stats are fresh.
 */
@Component({
  selector: 'app-referral-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './referral-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // All interactive elements in the popup show a pointer cursor (scoped to this component).
  styles: [`button, a { cursor: pointer; }`],
})
export class ReferralModalComponent {
  private readonly scrollLock = inject(ScrollLockService);
  private readonly api = inject(ReferralApi);
  private readonly toast = inject(ToastrService);
  private readonly modal = inject(ReferralModalService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isOpen = this.modal.isOpen;
  protected readonly loading = signal(true);
  protected readonly error = signal(false);
  protected readonly referral = signal<MyReferral | null>(null);
  protected readonly tab = signal<ReferralTab>('invite');
  protected readonly connecting = signal(false);
  /** Whether the "How does it work?" info panel is showing (opened via the ⓘ icon). */
  protected readonly showInfo = signal(false);
  /** True while the body is faded out mid-switch — see setTab. */
  protected readonly bodySettling = signal(false);
  private tabFade: ReturnType<typeof setTimeout> | null = null;
  /** Which value was just copied, so the button can show inline "Copied!" feedback (no toast). */
  protected readonly copiedTarget = signal<'link' | 'code' | null>(null);

  /** Every open/retry pushes here; switchMap cancels any in-flight request so a slow, stale
   *  response from a previous open can never overwrite the current one. */
  private readonly loadTrigger = new Subject<void>();

  constructor() {
    // One line for the whole body-scroll contract — see ScrollLockService.bindTo.
    this.scrollLock.bindTo(this.isOpen);
    // A pending fade would otherwise fire into a destroyed component's signals.
    this.destroyRef.onDestroy(() => {
      if (this.tabFade) clearTimeout(this.tabFade);
    });
    // Single reactive fetch pipeline. switchMap cancels the previous GET on each new trigger, so
    // rapid close/reopen or a retry mid-flight can't produce out-of-order writes. A per-request
    // catchError keeps the outer stream alive (an error maps to a null body → error state).
    this.loadTrigger
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.error.set(false);
        }),
        switchMap(() =>
          this.api.getMyReferral().pipe(catchError(() => of<MyReferralResponse>({ data: null }))),
        ),
        takeUntilDestroyed(),
      )
      .subscribe((res) => {
        // A 2xx with a null body (or a caught error) is "no data to show" — surface the error
        // state (with retry) rather than a blank modal body.
        if (res.data) {
          this.referral.set(res.data);
        } else {
          this.error.set(true);
        }
        this.loading.set(false);
      });

    // Fetch fresh data whenever the popup opens; reset to the Invite tab. Also lock the page
    // behind the modal so the background can't scroll while it's open (restored on close).
    effect(() => {
      const open = this.isOpen();
      if (typeof document !== 'undefined') {
        document.body.style.overflow = open ? 'hidden' : '';
      }
      if (open) {
        if (this.tabFade) {
          clearTimeout(this.tabFade);
          this.tabFade = null;
        }
        this.bodySettling.set(false);
        this.tab.set('invite');
        this.showInfo.set(false);
        this.load();
      }
    });

    // The modal is mounted once in the shell and its open-state lives in a root service, so it
    // would otherwise stay open across page navigations. Close it on any route change so it only
    // ever appears on the page where the user opened it.
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.modal.close());
  }

  protected readonly shareText = computed(() => {
    const link = this.referral()?.share_link ?? '';
    return `I use HashBooks to track my crypto taxes — sign up with my link: ${link}`;
  });

  protected readonly shareUrls = computed(() => {
    const link = this.referral()?.share_link ?? '';
    const encLink = encodeURIComponent(link);
    const encText = encodeURIComponent(this.shareText());
    const title = encodeURIComponent('Track your crypto taxes with HashBooks');
    return {
      twitter: `https://twitter.com/intent/tweet?text=${title}&url=${encLink}`,
      email: `mailto:?subject=${encodeURIComponent('Try HashBooks for crypto taxes')}&body=${encText}`,
      telegram: `https://t.me/share/url?url=${encLink}&text=${title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encLink}`,
      whatsapp: `https://wa.me/?text=${encText}`,
    };
  });

  /** Open + the retry button both funnel through here; the switchMap pipeline (constructor) owns
   *  the request lifecycle, cancelling any in-flight fetch so responses can't land out of order. */
  protected load(): void {
    this.loadTrigger.next();
  }

  /**
   * Switches tab behind a short fade rather than swapping the body in a single frame.
   *
   * `min-h` already stops the card shrinking below the taller tab, but Rewards can still exceed
   * that once a few referrals exist, and the two tabs share no layout at all — a heading, a link
   * box and a row of share icons being replaced by stat tiles and a table is a complete redraw.
   * Changing it at the bottom of a fade means the reader sees the new tab arrive rather than the
   * old one being torn out, and any height change the min-h cannot absorb happens while dimmed.
   */
  protected setTab(tab: ReferralTab): void {
    if (tab === this.tab()) return;
    if (this.tabFade) clearTimeout(this.tabFade);
    this.bodySettling.set(true);
    // Matches the `duration-150` on the body, so the swap lands where it is dimmest.
    this.tabFade = setTimeout(() => {
      this.tabFade = null;
      this.tab.set(tab);
      this.bodySettling.set(false);
    }, TAB_FADE_MS);
  }

  /**
   * "Add payment details" — kick off payout onboarding and hand the user to the
   * payment partner's hosted page (their banking details are entered on the partner, never here).
   */
  protected addPaymentDetails(): void {
    if (this.connecting()) return;
    this.connecting.set(true);
    this.api.createConnectOnboarding().subscribe({
      next: (res) => {
        // The backend is the primary trust boundary, but validate the server-provided URL before a
        // top-level navigation anyway: require https: + an allowed partner host (or same-origin path),
        // so a backend misconfig can't turn this into an open-redirect / phishing hop.
        const url = res.data?.url;
        if (url && this.isSafeOnboardingUrl(url)) {
          window.location.href = url; // leave the app for partner-hosted onboarding or redirect
        } else {
          this.connecting.set(false);
          this.toast.error('Payouts aren’t available yet. Please try again later.', 'Not available');
        }
      },
      error: () => {
        this.connecting.set(false);
        this.toast.error('Payouts aren’t available yet. Please try again later.', 'Not available');
      },
    });
  }

  /**
   * True only for a safe relative path, same-origin URL, or an https URL on an allowed domain (Razorpay / Stripe).
   */
  private isSafeOnboardingUrl(raw: string): boolean {
    if (raw.startsWith('/') && !raw.startsWith('//')) {
      return true;
    }
    try {
      const url = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      if (typeof window !== 'undefined' && url.origin === window.location.origin) {
        return true;
      }
      if (url.protocol !== 'https:') return false;
      const host = url.hostname.toLowerCase();
      return (
        host === 'razorpay.com' ||
        host.endsWith('.razorpay.com') ||
        host === 'rzp.io' ||
        host.endsWith('.rzp.io') ||
        host === 'stripe.com' ||
        host.endsWith('.stripe.com')
      );
    } catch {
      return false;
    }
  }

  protected close(): void {
    this.modal.close();
  }

  /** Copy to clipboard with inline button feedback (no toast). Reverts after 2s. */
  protected copy(value: string | undefined, target: 'link' | 'code'): void {
    if (!value || !navigator?.clipboard?.writeText) return;
    navigator.clipboard
      .writeText(value)
      .then(() => {
        this.copiedTarget.set(target);
        setTimeout(() => {
          if (this.copiedTarget() === target) this.copiedTarget.set(null);
        }, 2000);
      })
      .catch(() => this.toast.error('Couldn’t copy — please copy it manually.', 'Copy failed'));
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.isOpen()) this.close();
  }
}
